/**
 * Simpleheat is a tiny JavaScript library for drawing heatmaps with Canvas.
 * TS implementation of Simpleheat library.
 * https://github.com/mourner/simpleheat/blob/gh-pages/simpleheat.js
 *
 */
type SimpleheatPoint = [number, number] | [number, number, number];

export default class Simpleheat {
	private _canvas: HTMLCanvasElement;
	private _ctx: CanvasRenderingContext2D;
	private _width: number;
	private _height: number;
	private _max: number = 1;
	private _data: SimpleheatPoint[] = [];
	private _circle?: HTMLCanvasElement;
	private _r: number = 1;
	private _grad: Uint8ClampedArray = new Uint8ClampedArray(0);
	private defaultRadius: number = 25;
	private defaultGradient: { [key: number]: string } = {
		0.4: 'blue',
		0.6: 'cyan',
		0.7: 'lime',
		0.8: 'yellow',
		1.0: 'red',
	};

	constructor(canvas: HTMLCanvasElement | string) {
		this._canvas = typeof canvas === 'string' ? (document.getElementById(canvas) as HTMLCanvasElement) : canvas;

		if (!this._canvas) {
			throw new Error('SimpleHeat: canvas is not defined');
		}

		const ctx = this._canvas.getContext('2d');
		if (!ctx) {
			throw new Error('SimpleHeat: could not get canvas context');
		}

		this._ctx = this._canvas.getContext('2d') as CanvasRenderingContext2D;
		this._width = this._canvas.width;
		this._height = this._canvas.height;

		this.radius(this.defaultRadius);
		this.gradient(this.defaultGradient);
	}

	data(points: SimpleheatPoint[]) {
		this._data = points;
		return this;
	}

	max(max: number) {
		this._max = max;
		return this;
	}

	add(point: SimpleheatPoint) {
		this._data.push(point);
		return this;
	}

	clear() {
		this._data = [];
		return this;
	}

	radius(r: number, blur: number = 15) {
		this._circle = this._createCanvas();
		this._r = r + blur;
		this._circle.width = this._circle.height = this._r * 2;

		const ctx = this._circle.getContext('2d');
		if (!ctx) {
			throw new Error('SimpleHeat: could not create circle context');
		}

		ctx.shadowOffsetX = ctx.shadowOffsetY = this._r * 2;
		ctx.shadowBlur = blur;
		ctx.shadowColor = 'black';

		ctx.beginPath();
		ctx.arc(-this._r, -this._r, r, 0, Math.PI * 2, true);
		ctx.closePath();
		ctx.fill();

		return this;
	}

	resize() {
		this._width = this._canvas.width;
		this._height = this._canvas.height;
	}

	gradient(grad: { [key: number]: string }) {
		// create a 256x1 gradient that we'll use to turn a grayscale heatmap into a colored one
		const canvas = this._createCanvas();
		const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
		const gradient = ctx.createLinearGradient(0, 0, 0, 256);

		canvas.width = 1;
		canvas.height = 256;

		for (var i in grad) {
			gradient.addColorStop(+i, grad[i]);
		}

		ctx.fillStyle = gradient;
		ctx.fillRect(0, 0, 1, 256);

		this._grad = ctx.getImageData(0, 0, 1, 256).data;

		return this;
	}

	draw(minOpacity: number = 0.05) {
		const ctx = this._ctx;
		ctx.clearRect(0, 0, this._width, this._height);
		if (!this._circle) {
			this.radius(this.defaultRadius);
		}
		for (let i = 0, len = this._data.length, p; i < len; i++) {
			p = this._data[i];
			ctx.globalAlpha = Math.min(Math.max((p[2] ?? 1) / this._max, minOpacity), 1);
			ctx.drawImage(this._circle!, p[0] - this._r, p[1] - this._r);
		}

		const colored = ctx.getImageData(0, 0, this._width, this._height);
		this._colorize(colored.data, this._grad);
		ctx.putImageData(colored, 0, 0);

		return this;
	}

	private _colorize(pixels: Uint8ClampedArray, gradient: Uint8ClampedArray) {
		for (let i = 0, len = pixels.length, j; i < len; i += 4) {
			j = pixels[i + 3] * 4; // get gradient color from opacity value

			if (j) {
				pixels[i] = gradient[j];
				pixels[i + 1] = gradient[j + 1];
				pixels[i + 2] = gradient[j + 2];
			}
		}
	}

	private _createCanvas(): HTMLCanvasElement {
		return document.createElement('canvas');
	}
}
